<template>
  <div class="flex flex-col h-full bg-neutral-50">
    <!-- Styled Header -->
    <div 
      class="px-4 py-3 bg-white"
      :style="{
        borderBottom: '1px solid #e5e7eb'
      }"
    >
      <div class="flex justify-between items-center">
        <!-- Left side: Empty space -->
        <div class="flex items-center w-10"></div>

        <!-- Center: Title -->
        <div class="text-center flex-1">
          <h1 class="text-xl font-semibold text-neutral-800">FAQ</h1>
        </div>

        <!-- Right side: Mobile close button only -->
        <div class="flex items-center">
          <!-- Mobile: Show close button -->
          <button
            v-if="isMobile"
            @click="$emit('close-widget')"
            class="cursor-pointer text-neutral-600 hover:text-neutral-800 transition-colors duration-200"
          >
            <MaCross class="h-5 w-5" />
          </button>

          <!-- Desktop: Empty space (no button) -->
          <div v-else class="w-10 h-10"></div>
        </div>
      </div>
    </div>

    <!-- Styled Search -->
    <div 
      class="px-4 py-3 bg-white"
      :style="{
        borderBottom: '1px solid #e5e7eb'
      }"
    >
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <ClSearchMagnifyingGlass class="h-4 w-4 text-neutral-400" />
        </div>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Suche FAQ's..."
          class="w-full pl-10 pr-3 py-2 text-xs text-gray-600 bg-gray-50 rounded-md transition-all duration-200 focus:outline-none focus:bg-white focus:ring-1 focus:ring-gray-400"
          :style="{
            border: '1px solid #e5e7eb'
          }"
        />
      </div>
    </div>

    <!-- Categories List -->
    <div class="flex-1 overflow-y-auto px-4 py-2">
      <!-- Loading state -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center py-12"
      >
        <div class="w-6 h-6 border-2 border-neutral-200 border-t-blue-500 rounded-full animate-spin"></div>
        <p class="text-neutral-500 text-sm mt-3">Laden...</p>
      </div>

      <!-- No categories state -->
      <div
        v-else-if="filteredCategoriesWithIcons.length === 0"
        class="flex flex-col items-center justify-center py-16 text-center"
      >
        <div
          class="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mb-4"
          :style="{
            border: '1px solid #e5e7eb'
          }"
        >
          <IcEmojiThinkRight class="h-6 w-6 text-neutral-500" />
        </div>
        <h3 class="text-lg font-medium text-neutral-900 mb-2">
          Noch keine FAQs verfügbar
        </h3>
        <p class="text-neutral-500 text-sm">
          Momentan haben wir hier noch keine FAQ's hinterlegt
          – <span class="font-bold">aber wir arbeiten daran!</span>
        </p>
      </div>

      <!-- Clean Category List -->
      <div v-else class="space-y-2">
        <p class="text-xs text-neutral-500 mb-4 px-1 mt-2">
          {{ filteredCategoriesWithIcons.length }} Kategorien
        </p>

        <div
          v-for="categoryData in filteredCategoriesWithIcons"
          :key="categoryData.category"
          class="category-item"
          :style="{
            backgroundColor: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }"
          @click="viewCategory(categoryData.category)"
        >
          <div class="p-3">
            <div class="flex items-center">
              <!-- Dynamic Icon with custom colors -->
              <div
                class="w-8 h-8 rounded-lg flex items-center justify-center mr-3 flex-shrink-0"
                :style="getIconStyles()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  :style="{ color: getIconColor() }"
                  v-html="categoryData.icon"
                ></svg>
              </div>

              <!-- Category Name -->
              <div class="flex-1 min-w-0">
                <h3 class="text-sm font-medium text-neutral-800">
                  {{ categoryData.displayName }}
                </h3>
              </div>

              <!-- Simple Arrow -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-neutral-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFAQCategories } from "../../lib/appwriteFaq";
import {
  ClSearchMagnifyingGlass,
  MaCross,
  IcEmojiThinkRight,
} from "@kalimahapps/vue-icons";

export default {
  name: "FAQCategoriesScreen",
  components: {
    ClSearchMagnifyingGlass,
    MaCross,
    IcEmojiThinkRight,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    // Add styles prop to get colors from ChatWidget
    styles: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["view-category", "change-screen", "close-widget"],
  data() {
    return {
      categories: [],
      loading: true,
      searchQuery: "",
      showDevTools:
        process.env.NODE_ENV === "development" ||
        window.location.hostname === "localhost",

      // Define the category mappings from your sidebar
      categoryMappings: {
        // German name -> category data
        "Erste Schritte": {
          id: "getting-started",
          name: "Erste Schritte",
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />',
        },
        Konto: {
          id: "account",
          name: "Konto",
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />',
        },
        Abrechnung: {
          id: "billing",
          name: "Abrechnung",
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />',
        },
        Funktionen: {
          id: "features",
          name: "Funktionen",
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />',
        },
      },
    };
  },
  async mounted() {
    await this.loadCategories();
  },
  computed: {
    // Show ALL categories - with custom icons for default ones, ? icon for others
    categoriesWithIcons() {
      return this.categories.map((category) => {
        const formattedName = this.formatCategoryName(category);
        const mappingData = this.categoryMappings[formattedName];

        if (mappingData) {
          // Default category with custom icon
          return {
            category: category,
            displayName: formattedName,
            icon: mappingData.icon,
            id: mappingData.id,
            isDefault: true,
          };
        } else {
          // Custom/other category with ? icon
          return {
            category: category,
            displayName: formattedName,
            icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />',
            id: category,
            isDefault: false,
          };
        }
      });
    },

    filteredCategoriesWithIcons() {
      if (!this.searchQuery) {
        return this.categoriesWithIcons;
      }

      const query = this.searchQuery.toLowerCase();
      return this.categoriesWithIcons.filter((categoryData) =>
        categoryData.displayName.toLowerCase().includes(query)
      );
    },
  },
  methods: {
    async loadCategories() {
      this.loading = true;

      try {
        this.categories = await getFAQCategories();
        console.log("Loaded categories:", this.categories);
        console.log("Categories with icons:", this.categoriesWithIcons);
      } catch (error) {
        console.error("Error loading categories:", error);
        this.categories = [];
      } finally {
        this.loading = false;
      }
    },

    viewCategory(category) {
      this.$emit("view-category", category);
    },

    getIconStyles() {
      const bubbleColor = this.styles?.bubbleColor || "#1a1a1a";

      // Convert hex to RGB and add opacity
      const hex = bubbleColor.replace("#", "");
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);

      return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.1)`, // 10% opacity for subtle background
      };
    },

    getIconColor() {
      // Use bubble color at 100% opacity for the icon itself
      return this.styles?.bubbleColor || "#1a1a1a";
    },

    formatCategoryName(category) {
      if (!category) return "General";

      return category
        .replace(/[-_]/g, " ")
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
};
</script>

<style scoped>
/* Minimal styles - only animations that can't be done inline */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>